import moment from "moment-timezone";
import { OtcContractLayerDto } from "@/lib/api/otc";
import {
  convertFormatMomentToLocalTimezone,
  formatDateWithoutTimezoneToDate,
  safeParseMomentWithFormats,
} from "@/lib/api/date";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { contractToForm } from "../contract-form-v2/transform";
import { OtcFormValues } from "../contract-form-v2/types";

/**
 * Form-value shape used by the layer-native v3 editor.
 *
 * It is a superset of the v2 `OtcFormValues` because the current layer can own
 * fields that are NOT `trading_contract` columns and therefore never appear in
 * a contract snapshot or in v2's form shape. The clearest example is
 * `exit_date`, which is layer/event metadata.
 */
export interface OtcLayerFormValues extends OtcFormValues {
  exit_date?: Date;
  exit_quantity?: string;
  exit_final_futures_price?: string;
  roll_partial_quantity?: string;
}

/**
 * Layer metadata date fields that are not contract columns. They are not
 * produced by `contractToForm` (which only reads scalar contract fields), so
 * they must be seeded from the layer DTO's `field_patch`.
 */
const LAYER_METADATA_DATE_FIELDS = ["exit_date"] as const;

/**
 * Layer metadata decimal-string fields that are not contract columns. Like the
 * date metadata, they are seeded from `field_patch` (not the snapshot).
 *
 * IMPORTANT: `exit_quantity` is the amount exited by this amendment and lives
 * in `field_patch.exit_quantity`. It is distinct from `snapshot.quantity`,
 * which is the remaining contract quantity. They must not be conflated.
 */
const LAYER_METADATA_SCALAR_FIELDS = [
  "exit_quantity",
  "exit_final_futures_price",
] as const;

/**
 * Parses a `field_patch` scalar (decimal string / number) into the string form
 * the v3 float inputs use. Returns undefined for empty/missing values.
 */
function parseLayerMetadataScalar(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "number") return String(value);
  if (typeof value === "string" && value.trim() !== "") return value;
  return undefined;
}

/**
 * Parses a scalar ISO/date-like string into a Date, mirroring v2's date
 * parsing conventions (timezone-aware, via moment) so values round-trip
 * consistently with the rest of the OTC forms.
 */
export function parseLayerDate(value: unknown): Date | undefined {
  if (value == null) return undefined;
  if (value instanceof Date) return formatDateWithoutTimezoneToDate(value);
  if (typeof value === "string" && value !== "") {
    const formats = [
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "YYYY-MM-DDTHH:mm:ss",
      moment.ISO_8601,
    ].reverse();
    const parsed = safeParseMomentWithFormats(
      value,
      formats,
      CommonDestinationTimezone,
    );
    return parsed
      ? convertFormatMomentToLocalTimezone(parsed)
      : formatDateWithoutTimezoneToDate(new Date(value));
  }
  return undefined;
}

/**
 * Adapts a current layer DTO into editable v3 form values.
 *
 * - Contract scalar fields come from the layer `snapshot` via the v2
 *   `contractToForm` transform (a pure scalar mapping; safe to reuse).
 * - Layer metadata fields (e.g. `exit_date`) are seeded from `field_patch`,
 *   since the DTO does not expose layer `meta_data` and these are not contract
 *   columns.
 *
 * Use this only for the current editable layer. Historical/locked layers are
 * rendered read-only directly from their own snapshot/field_patch and are not
 * adapted into a form.
 */
export function layerToFormValues(layer: OtcContractLayerDto): OtcLayerFormValues {
  const base = contractToForm(layer.snapshot);
  const values: OtcLayerFormValues = { ...base };

  for (const field of LAYER_METADATA_DATE_FIELDS) {
    const patched = layer.field_patch[field];
    const parsed = parseLayerDate(patched);
    if (parsed) {
      values[field] = parsed;
    }
  }

  for (const field of LAYER_METADATA_SCALAR_FIELDS) {
    const parsed = parseLayerMetadataScalar(layer.field_patch[field]);
    if (parsed !== undefined) {
      values[field] = parsed;
    }
  }

  values.roll_partial_quantity = parseLayerMetadataScalar(layer.meta_data?.roll_partial_quantity);

  return values;
}
